'use client'

import dynamic from 'next/dynamic'
import { C, Callout, Exercise, Figure, H, Lesson, M, MB, P, Quiz, Step, UL } from '../_components/lesson'
import * as EX from './exercises/frontier-extra'

const PatchTokens = dynamic(() => import('../_components/widgets/FrontierExtraWidgets').then(m => m.PatchTokens), { ssr: false })
const ContrastiveTrainer = dynamic(() => import('../_components/widgets/FrontierExtraWidgets').then(m => m.ContrastiveTrainer), { ssr: false })

export default function Multimodal() {
  return (
    <Lesson>
      <Step>
        <H>An image is worth 16 × 16 words</H>
        <P>
          Transformers read sequences of tokens. To make one see, turn an image into a sequence: cut it into square{' '}
          <strong>patches</strong>, flatten each patch into a vector, and pass it through a learned linear layer. That gives one
          token per patch. Add position embeddings so the model knows where each patch came from, and the rest is exactly the
          transformer you built. That’s the <strong>Vision Transformer</strong> (ViT, 2020).
        </P>
        <Figure n="10.7" wide caption="The same picture as a grid and as a token sequence. Smaller patches mean more tokens: more detail, but more attention to compute.">
          <PatchTokens />
        </Figure>
      </Step>

      <Step gate={['patchify', 'q-patch']}>
        <H>Patchify</H>
        <Exercise
          id="patchify"
          title="patchify"
          {...EX.patchify}
          hints={[
            <>
              Step <C>r</C> and <C>c</C> by <C>p</C>; for each patch push <C>{'img[r + i][c + j]'}</C> for <C>i</C>, <C>j</C>{' '}
              from 0 to p − 1.
            </>,
          ]}
        />
        <Quiz
          id="q-patch"
          question="A 224 × 224 image in 16 × 16 patches. How many tokens, and how does the attention cost change if you halve the patch size?"
          options={[
            { label: '196 tokens; halving the patch size quadruples the tokens, so attention costs about 16× more', correct: true },
            { label: '196 tokens; halving the patch size doubles the cost', why: 'Halving the patch side gives 4× as many patches, and attention compares every pair: 4² = 16.' },
            { label: '14 tokens', why: '224 / 16 = 14 patches per side, so 14 × 14 = 196.' },
          ]}
        />
      </Step>

      <Step>
        <H>One space for pictures and words</H>
        <P>
          How do you connect images to language? <strong>CLIP</strong> (2021) trained an image encoder and a text encoder side
          by side on about 400 million image–caption pairs from the web. The goal: put each image near its own caption in a
          shared embedding space, and far from everyone else’s.
        </P>
        <P>
          In each batch of N pairs, the model computes all N × N similarities. The N true pairs sit on the diagonal. The loss is
          plain cross-entropy, done twice: each row should pick its own caption, and each column its own image.
        </P>
        <MB>{'L = \\tfrac{1}{2}\\big(\\text{CE}_{\\text{rows}}(S/\\tau) + \\text{CE}_{\\text{columns}}(S/\\tau)\\big)'}</MB>
        <Figure n="10.8" wide caption="Image and caption features start in different “languages”. Training learns two small projections into a shared space. Watch the diagonal light up.">
          <ContrastiveTrainer />
        </Figure>
      </Step>

      <Step gate="contrastive">
        <H>The contrastive loss</H>
        <P>
          <M>{'\\tau'}</M> (“tau”) is a <strong>temperature</strong>, just like in sampling: dividing by a small τ sharpens the
          softmax, so the model must separate the right pair clearly.
        </P>
        <Exercise
          id="contrastive"
          title="clipLoss"
          {...EX.contrastive}
          hints={[
            <>
              A helper: <C>{'const ce = (scores, target) => -Math.log(vec.softmax(scores.map(s => s / tau))[target])'}</C>.
            </>,
            <>
              For each i, add <C>ce(S[i], i)</C> (row i) and <C>{'ce(S.map(row => row[i]), i)'}</C> (column i). Divide by 2n.
            </>,
          ]}
        />
      </Step>

      <Step gate="zero-shot">
        <H>Classify anything, with no training</H>
        <P>
          Once images and text share a space, you get <strong>zero-shot classification</strong>: write each possible label as a
          caption (“a photo of a dog”), embed them, and pick the label closest to the image. No labelled training images
          needed, and the labels can be anything you can describe.
        </P>
        <Exercise id="zero-shot" title="zeroShot" {...EX.zeroShot} hints={[<><C>{'vec.argmax(labelEmb.map(l => vec.cosine(img, l)))'}</C></>]} />
        <UL>
          <li>
            <strong>Vision-language models</strong> plug an image encoder into a language model: image patches become tokens in
            the same sequence as the text, so the model can answer questions about pictures.
          </li>
          <li>
            <strong>Text-to-image</strong> generators steer a diffusion model (from the diffusion lesson) with text embeddings
            like CLIP’s.
          </li>
          <li>
            <strong>Audio</strong> becomes tokens too: slice the sound wave (or its spectrogram) into short frames.
          </li>
        </UL>
        <Callout kind="why">
          Tokens are the universal interface. Text, image patches, audio frames and actions can all be embedded as vectors and
          fed into the same transformer. That’s why one architecture now handles so many kinds of data.
        </Callout>
      </Step>
    </Lesson>
  )
}
